import FacebookLogin from 'react-facebook-login/dist/facebook-login-render-props';
import GoogleLogin, { GoogleLoginResponse, GoogleLoginResponseOffline } from 'react-google-login';
import { FormattedMessage } from 'react-intl';
import Apple from '@alltrails/denali/icons/Apple';
import Google from '@alltrails/denali/icons/Google';
import GoogleNoBackground from '@alltrails/denali/icons/GoogleNoBackground';
import { ReactFacebookLoginInfo } from 'react-facebook-login';
import logLogInScreenContinueButtonTapped from '@alltrails/amplitude/events/logLogInScreenContinueButtonTapped';
import logSignUpScreenContinueButtonTapped from '@alltrails/amplitude/events/logSignUpScreenContinueButtonTapped';
import classNames from 'classnames';
import FacebookButton from '../FacebookButton/FacebookButton';
import styles from './AltLoginsForm.module.scss';

type Props = {
  appleLoginHandler: (response: any) => void;
  facebookLoginHandler: (response: ReactFacebookLoginInfo) => void;
  googleLoginHandler: (response: GoogleLoginResponse | GoogleLoginResponseOffline) => void;
  authType: 'login' | 'signup';
  flatColors?: boolean;
};

const FACEBOOK_APP_ID = `${process.env.NEXT_PUBLIC_FACEBOOK_APP_ID}` || '';

// https://www.figma.com/file/gZaqAej8HzoFkyrjlQ4cNs/Integrating-Brand-Changes?node-id=1571%3A79911
const AltLoginsForm = ({ appleLoginHandler, facebookLoginHandler, googleLoginHandler, authType, flatColors = false }: Props) => {
  return (
    <div className={styles.content}>
      <GoogleLogin
        clientId={process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID}
        render={renderProps => (
          <button
            className={classNames(styles.google, { [styles.flatColors]: flatColors })}
            data-testid="loginWithGoogle"
            onClick={() => {
              if (authType === 'signup') {
                logSignUpScreenContinueButtonTapped({ signup_screen_continue_button_type: 'google' });
              } else {
                logLogInScreenContinueButtonTapped({ login_screen_continue_button_type: 'google' });
              }
              renderProps.onClick();
            }}
            type="button"
          >
            {flatColors ? <GoogleNoBackground className={styles.icon} /> : <Google className={styles.icon} />}
            <span className={styles.text}>
              <FormattedMessage defaultMessage="Continue with Google" />
            </span>
          </button>
        )}
        buttonText="Login"
        onSuccess={googleLoginHandler}
        // don't send onFailure to Bugsnag, these are expected events like popup_closed_by_user
        cookiePolicy="single_host_origin"
      />
      <FacebookLogin
        disableMobileRedirect
        appId={FACEBOOK_APP_ID}
        onClick={() => {
          if (authType === 'signup') {
            logSignUpScreenContinueButtonTapped({ signup_screen_continue_button_type: 'facebook' });
          } else {
            logLogInScreenContinueButtonTapped({ login_screen_continue_button_type: 'facebook' });
          }
        }}
        callback={(userInfo: ReactFacebookLoginInfo) => {
          // user closed or clicked Cancel on the popup before logging in
          if (!userInfo.accessToken) {
            return;
          }

          facebookLoginHandler(userInfo);
        }}
        scope="email,user_friends,user_gender,user_location"
        render={(renderProps: { onClick: () => void }) => (
          <FacebookButton onClick={renderProps.onClick} flatColors={flatColors}>
            <FormattedMessage defaultMessage="Continue with Facebook" />
          </FacebookButton>
        )}
      />
      <button
        className={classNames(styles.apple, { [styles.flatColors]: flatColors })}
        data-testid="loginWithApple"
        onClick={appleLoginHandler}
        type="button"
      >
        <Apple className={styles.icon} color={flatColors ? '--color-neutral-black' : '--color-neutral-white'} />
        <span className={styles.text}>
          <FormattedMessage defaultMessage="Continue with Apple" />
        </span>
      </button>
    </div>
  );
};

export default AltLoginsForm;
